/**
 * Guard CLI commands — evaluate/list/status for guardrail enforcement.
 *
 * These functions back the `guard evaluate`, `guard list`, and `guard status`
 * sub-commands that are auto-injected by @aaac/contracts cli-generator
 * whenever `governance.guardrails: true` is declared in component.yaml.
 *
 * exit code contract (evaluate):
 *   0 = allowed / pass / warn
 *   2 = denied (permission === "deny")
 */

import { readFile, readdir } from "node:fs/promises";
import YAML from "yaml";
import {
  buildGuardrailContext,
  type GuardrailCheckEntry,
} from "../generator/context.js";
import {
  createGuardrailHooksFromRules,
  type GuardrailCheckResult,
  type GuardrailRuleData,
  type HookResponse,
} from "./guardrail-hooks.js";
import { guardrailContextToRuleData } from "./bound-resolver.js";

// ---------------------------------------------------------------------------
// guard evaluate
// ---------------------------------------------------------------------------

export interface EvaluateGuardrailOptions {
  /** Raw JSON string read from stdin (HookContext format). */
  hookJson: string;
  /** Component agent-contracts DSL (contains guardrails + guardrail_policies). */
  dsl: Record<string, unknown>;
  /** Paths to software binding YAML files (contain guardrail_impl matchers). */
  bindingPaths: string[];
  /** Active guardrail policy name (from project.yaml active_guardrail_policy). */
  activeGuardrailPolicy?: string;
}

export interface EvaluateGuardrailResult {
  /** HookResponse to write to stdout. */
  response: HookResponse;
  /**
   * CLI exit code.
   *   0 = allowed (pass or warn)
   *   2 = denied (permission === "deny")
   */
  exitCode: 0 | 2;
  /** All check results from all matchers. */
  checks: GuardrailCheckResult[];
}

/**
 * Evaluate a guardrail hook from a JSON string (typically stdin).
 *
 * Detects the hook type from which fields are present in the input:
 * - `command` → beforeShellExecution
 * - `tool_name` / `tool_input` → preToolUse
 * - `file_path` (without command) → afterFileEdit
 */
export async function evaluateGuardrail(
  opts: EvaluateGuardrailOptions,
): Promise<EvaluateGuardrailResult> {
  let hookInput: Record<string, unknown>;
  try {
    hookInput = JSON.parse(opts.hookJson) as Record<string, unknown>;
  } catch (cause) {
    throw new Error(
      `Invalid hook JSON: input must be valid JSON. ${(cause as Error).message}`,
    );
  }

  // Build guardrail context from binding files
  let guardrailCtx: ReturnType<typeof buildGuardrailContext> = {
    commandChecks: [],
    fileChecks: [],
    contentChecks: [],
    allChecks: [],
    hasGuardrails: false,
  };

  for (const bindingPath of opts.bindingPaths) {
    let raw: string;
    try {
      raw = await readFile(bindingPath, "utf8");
    } catch (err) {
      // Binding file not found — skip gracefully
      const code = (err as NodeJS.ErrnoException).code;
      if (code === "ENOENT") continue;
      throw err;
    }
    const binding = YAML.parse(raw) as Record<string, unknown>;
    guardrailCtx = buildGuardrailContext(
      opts.dsl,
      binding,
      opts.activeGuardrailPolicy,
    );
  }

  const ruleData: GuardrailRuleData = guardrailContextToRuleData(guardrailCtx);
  const hooks = createGuardrailHooksFromRules(ruleData);

  // Dispatch to the appropriate hook based on input fields
  const command = hookInput.command as string | undefined;
  const toolName = hookInput.tool_name as string | undefined;
  const toolInput = hookInput.tool_input as
    | { file_path?: string; path?: string }
    | undefined;
  const filePath = hookInput.file_path as string | undefined;
  const content = hookInput.content as string | undefined;

  let response: HookResponse = {};
  let checks: GuardrailCheckResult[] = [];

  if (command !== undefined) {
    // Shell execution hook
    response = hooks.beforeShellExecution({ command });
    checks = hooks.runChecks({ command });
  } else if (toolName !== undefined || toolInput !== undefined) {
    // Pre-tool-use hook
    response = hooks.preToolUse({ tool_name: toolName, tool_input: toolInput });
    const checkFilePath =
      toolInput?.file_path ?? toolInput?.path ?? "";
    if (checkFilePath) {
      checks = hooks.runChecks({ filePath: checkFilePath });
    }
  } else if (filePath !== undefined) {
    // After-file-edit hook
    response = hooks.afterFileEdit({ file_path: filePath, content });
    checks = hooks.runChecks({ filePath, content });
  }
  // If no recognizable fields, response stays {} (pass)

  const exitCode: 0 | 2 = response.permission === "deny" ? 2 : 0;
  return { response, exitCode, checks };
}

// ---------------------------------------------------------------------------
// guard list
// ---------------------------------------------------------------------------

export interface ListGuardrailsOptions {
  /** Component agent-contracts DSL. */
  dsl: Record<string, unknown>;
  /** Paths to software binding YAML files. */
  bindingPaths: string[];
  /** Active guardrail policy name. */
  activeGuardrailPolicy?: string;
}

export interface GuardrailListEntry {
  guardrail_id: string;
  description: string;
  matcher_type: string;
  action: string;
  severity: string;
  pattern: string;
}

export interface ListGuardrailsResult {
  policy: string | undefined;
  entries: GuardrailListEntry[];
}

/**
 * List all active guardrails and their enforcement settings.
 *
 * Reads DSL + binding files and builds the guardrail context for the active
 * policy, returning a flat list of guardrail_id / matcher_type / action /
 * severity rows for display.
 */
export async function listGuardrails(
  opts: ListGuardrailsOptions,
): Promise<ListGuardrailsResult> {
  let guardrailCtx: ReturnType<typeof buildGuardrailContext> = {
    commandChecks: [],
    fileChecks: [],
    contentChecks: [],
    allChecks: [],
    hasGuardrails: false,
  };

  for (const bindingPath of opts.bindingPaths) {
    let raw: string;
    try {
      raw = await readFile(bindingPath, "utf8");
    } catch (err) {
      const code = (err as NodeJS.ErrnoException).code;
      if (code === "ENOENT") continue;
      throw err;
    }
    const binding = YAML.parse(raw) as Record<string, unknown>;
    guardrailCtx = buildGuardrailContext(
      opts.dsl,
      binding,
      opts.activeGuardrailPolicy,
    );
  }

  const entries: GuardrailListEntry[] = guardrailCtx.allChecks.map(
    (c: GuardrailCheckEntry) => ({
      guardrail_id: c.guardrail_id,
      description: c.description,
      matcher_type: c.matcher_type,
      action: c.action,
      severity: c.severity,
      pattern: c.pattern,
    }),
  );

  return {
    policy: opts.activeGuardrailPolicy,
    entries,
  };
}

// ---------------------------------------------------------------------------
// guard status
// ---------------------------------------------------------------------------

export interface GuardStatusOptions {
  /** Base directory for agent logs (defaults to ".agent-logs"). */
  baseDir?: string;
  /** Maximum number of recent run events to return (defaults to 10). */
  limit?: number;
}

export interface GuardStatusResult {
  hasHistory: boolean;
  message: string;
  /** Recent run log events (operation_start/end, etc.). */
  recentEvents: unknown[];
}

/**
 * Show recent guardrail evaluation summary.
 *
 * Phase 1 limitation: RunLogEvent does not yet include a `guardrail_evaluate`
 * event type, so this command reports general run history from .agent-logs/runs/.
 * Guard-specific event tracking will be added in a future phase.
 *
 * Gracefully returns "no evaluation history" when .agent-logs/ does not exist.
 */
export async function guardStatus(
  opts: GuardStatusOptions = {},
): Promise<GuardStatusResult> {
  const baseDir = opts.baseDir ?? ".agent-logs";
  const runsDir = `${baseDir}/runs`;
  const limit = opts.limit ?? 10;

  let files: string[] = [];
  try {
    files = await readdir(runsDir);
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code === "ENOENT") {
      return {
        hasHistory: false,
        message:
          "No evaluation history. Note: guardrail_evaluate event type is not yet tracked in Phase 1.",
        recentEvents: [],
      };
    }
    throw err;
  }

  const jsonlFiles = files
    .filter((f) => f.endsWith(".jsonl"))
    .sort()
    .reverse()
    .slice(0, limit);

  if (jsonlFiles.length === 0) {
    return {
      hasHistory: false,
      message: "No evaluation history.",
      recentEvents: [],
    };
  }

  const recentEvents: unknown[] = [];
  for (const file of jsonlFiles) {
    const filePath = `${runsDir}/${file}`;
    let raw: string;
    try {
      raw = await readFile(filePath, "utf8");
    } catch {
      continue;
    }
    const events = raw
      .split("\n")
      .filter((line) => line.trim().length > 0)
      .map((line) => {
        try {
          return JSON.parse(line) as unknown;
        } catch {
          return null;
        }
      })
      .filter(Boolean);
    recentEvents.push(...events);
    if (recentEvents.length >= limit) break;
  }

  const count = recentEvents.length;
  return {
    hasHistory: count > 0,
    message:
      count > 0
        ? `${count} recent run event(s) found. Note: guardrail_evaluate events are not yet tracked in Phase 1.`
        : "No evaluation history.",
    recentEvents: recentEvents.slice(0, limit),
  };
}
